import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import { timeFormat } from '../../../constants/time';
import { getTechnicalWarrantyOptions, getWarrantyExpiredSubcategoryOptions } from '../../JobSummary/constants';

import {
  Avatar,
  Grid,
} from '../../base';

const getTechnicalWarrantyCategoryLabel = (categoryValue, translations) => {
  if (!categoryValue) return '';
  const tw = translations.TechnicalWarranty || translations;
  const fromExpired = getWarrantyExpiredSubcategoryOptions(tw).find(o => o.value === categoryValue);
  if (fromExpired) return fromExpired.label;
  const fromDenied = getTechnicalWarrantyOptions('DENIED', tw).find(o => o.value === categoryValue);
  if (fromDenied) return fromDenied.label;
  const fromAccepted = getTechnicalWarrantyOptions('ACCEPTED', tw).find(o => o.value === categoryValue);
  if (fromAccepted) return fromAccepted.label;
  return tw[categoryValue] || categoryValue;
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  avatar: {
    fontSize: theme.typography.caption.fontSize,
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
  },
  userRow: {
    marginBottom: theme.spacing(1),
  },
  infoRow: {
    marginBottom: theme.spacing(1),
    color: 'rgba(0, 0, 0, 0.7)',
  },
  timeRow: {
    fontSize: 12,
    fontWeight: 300,
  },
  additionalRow: {
    marginBottom: theme.spacing(1),
    marginTop: 0,
    color: 'rgba(0, 0, 0, 0.7)',
  },
  infoDiv: {
    marginBottom: theme.spacing(1)
  }
}));

const renderAdditionalInfo = (data, gt) => {
  switch (data.action) {
    case 'REQUEST_TECHNICAL_WARRANTY_DECISION':
    case 'APPROVE_TECHNICAL_WARRANTY_DECISION':
    case 'DENY_TECHNICAL_WARRANTY_DECISION':
    case 'REFACTOR_TECHNICAL_WARRANTY_DECISION':
    case 'AMEND_APPROVED_TECHNICAL_DECISION':
    case 'DELETE_APPROVED_TECHNICAL_DECISION': {
      const statusLabel = gt.TechnicalWarranty[data.technicalDecisionStatus] || data.technicalDecisionStatus || '';
      const proposedLabel = data.technicalDecisionProposed
        ? getTechnicalWarrantyCategoryLabel(data.technicalDecisionProposed, gt)
        : '';
      const suffix = proposedLabel ? ` - ${proposedLabel}` : '';
      return ` (${statusLabel}${suffix})`;
    }
    case 'REQUEST_REPORT_LEVEL_CHANGE':
    case 'APPROVE_REPORT_LEVEL_CHANGE':
    case 'REQUEST_COMMERCIAL_WARRANTY_DECISION':
    case 'DENY_COMMERCIAL_WARRANTY_DECISION': {
      const categoryLabelReq = data.commercialDecisionCategory
        ? (gt.CommercialWarranty[data.commercialDecisionCategory] || data.commercialDecisionCategory)
        : '';
      return data.commercialDecisionCategory
        ? ` (${gt.CommercialWarranty[data.commercialDecisionApproved]} - ${categoryLabelReq}, ${gt.Forms.fields.price}: $${data.price})`
        : ` (${gt.CommercialWarranty[data.commercialDecisionApproved]}, ${gt.Forms.fields.price}: $${data.price})`;
    }
    case 'DENY_REPORT_LEVEL_CHANGE':
      return ` (${gt.from} ${data.previousLevel} ${gt.to} ${data.currentLevel})`;
    case 'APPROVE_COMMERCIAL_WARRANTY_DECISION':
    case 'NULLIFY_COMMERCIAL_WARRANTY_DECISION':
    case 'AMEND_APPROVED_COMMERCIAL_DECISION': {
      const categoryLabelAmend = data.commercialDecisionCategory
        ? (gt.CommercialWarranty[data.commercialDecisionCategory] || data.commercialDecisionCategory)
        : '';
      return data.commercialDecisionCategory
        ? ` (${gt.CommercialWarranty[data.commercialDecisionApproved]} - ${categoryLabelAmend}, ${gt.Forms.fields.price}: $${data.price})`
        : ` (${gt.CommercialWarranty[data.commercialDecisionApproved]}, ${gt.Forms.fields.price}: $${data.price})`;
    }
    case 'DELETE_APPROVED_COMMERCIAL_DECISION': {
      const categoryLabelDel = data.commercialDecisionCategory
        ? (gt.CommercialWarranty[data.commercialDecisionCategory] || data.commercialDecisionCategory)
        : '';
      return data.commercialDecisionCategory
        ? ` (${gt.CommercialWarranty[data.commercialDecisionApproved]} - ${categoryLabelDel}, ${gt.Forms.fields.price}: $${data.price})`
        : ` (${gt.CommercialWarranty[data.commercialDecisionApproved]}, ${gt.Forms.fields.price}: $${data.price})`;
    }
    case 'SET_PRICE':
      return `($${data.price})`;
    case 'ADD_TEAM_MEMBER':
    case 'REMOVE_TEAM_MEMBER':
      return `(${data.teamMember})`;
    case 'JOB_RELEASED_STATUS_SET':
    case 'CHOOSE_RELEASE_METHOD':
      return data.releaseMethod ? `(${gt.Release[data.releaseMethod]})` : null
    case 'GENERATE_ALERT':
      const translation = gt.Alerts[data.alertType];
      return translation && ` (${gt.Alerts[data.alertType]})`;
    case 'RATE_TECHNICAL_DECISION':
      return data.rateDecision === 'Agree'
        ? gt.TechnicalWarranty.AGREE_WITH_TECHNICAL_WARRANTY_LOG
        : gt.TechnicalWarranty.DISAGREE_WITH_TECHNICAL_WARRANTY_LOG
    case 'INITIAL_WARRANTY_CODE_EDITED':
      return `(${gt.from} ${data.previousInitialWarrantyCode} ${gt.to} ${data.initialWarrantyCode})`;
    default:
      return
  }
}

const Log = ({ t, gt, data }) => {
  const c = useStyles();
  const { user, partNumbers, shipToAddress, serviceInformationBulletin, reason, initialWarrantyCode, timeOfAction, action, rateComment, previousInitialWarrantyCode } = data;

  return (
    <Grid item xs={12} className={c.container}>
      <Avatar className={c.avatar} user={user} />
      <div className={c.infoContainer}>
        <div className={c.userRow}>{user}</div>
        <div className={c.infoDiv}>
          <div className={c.infoRow}>{gt.Actions[action]} <span>{renderAdditionalInfo(data, gt)}</span></div>
          {partNumbers && <p className={c.additionalRow}>part numbers: {partNumbers}</p>}
          {shipToAddress && <p className={c.additionalRow}>ship to address: {shipToAddress}</p>}
          {serviceInformationBulletin && <p className={c.additionalRow}>{t.sib}: {serviceInformationBulletin}</p>}
          {reason && <p className={c.additionalRow}>{gt.reason}: {reason}</p>}
          {initialWarrantyCode && !previousInitialWarrantyCode && <p className={c.additionalRow}>{t.initialWarrantyCode}: {initialWarrantyCode}</p>}
          {rateComment && <p className={c.additionalRow}>{gt.comment}: {rateComment}</p>}
        </div>
        <div className={c.timeRow}>
          <time dateTime={timeOfAction}>{moment(timeOfAction).format(timeFormat.MINUTES)}</time>
        </div>
      </div>
    </Grid>
  );
}


export default Log;



// WEBPACK FOOTER //
// ./src/components/LogSection/Log/Log.js